import PropTypes from "prop-types"
import moment from "moment"
import { Descriptions } from "antd"
import { useTranslation } from "react-i18next"
import { BILLING_STANDING, CONNECTION_STATUS } from "constants/powertranz"
import PowerTranzStatus from "../PowerTranzStatus"

// Read-only summary of what the API reports for the merchant account. Never shows credentials.
const StatusSummary = ({ account }) => {
  const { t } = useTranslation()

  let verification = t("powertranz.settings.summary.not_verified")
  if (account.connectionStatus === CONNECTION_STATUS.INVALID) verification = t("powertranz.settings.summary.verification_failed")
  else if (account.verifiedAt) verification = t("powertranz.settings.summary.verified_on", { date: moment(account.verifiedAt).format("lll") })

  const items = [
    {
      key: "connection",
      label: t("powertranz.settings.summary.connection"),
      children: <PowerTranzStatus connectionStatus={account.connectionStatus} />
    },
    { key: "verification", label: t("powertranz.settings.summary.verification"), children: verification },
    // Shown once the API sends them; until then the summary simply leaves them out.
    account.idLast4 && {
      key: "merchant_id",
      label: t("powertranz.settings.summary.merchant_id"),
      children: t("powertranz.settings.summary.merchant_id_value", { last4: account.idLast4 })
    },
    account.checkoutPaymentProvider && {
      key: "checkout_provider",
      label: t("powertranz.settings.summary.checkout_provider"),
      children: t(`powertranz.settings.summary.providers.${account.checkoutPaymentProvider}`, { defaultValue: account.checkoutPaymentProvider })
    },
    // Only worth a line when it stops payments; the state alert explains what to do.
    account.billingStanding === BILLING_STANDING.SUSPENDED && {
      key: "billing",
      label: t("powertranz.settings.summary.billing_standing"),
      children: t("powertranz.settings.summary.billing_suspended")
    },
    (account.pageSet || account.pageName) && {
      key: "hosted_page",
      label: t("powertranz.settings.summary.hosted_page"),
      children: `${account.pageSet} / ${account.pageName}`
    },
    account.isConnected && {
      key: "saved_cards",
      label: t("powertranz.settings.summary.saved_cards"),
      children: account.isTokenizationEnabled ? t("powertranz.settings.tokenization_on") : t("powertranz.settings.tokenization_off")
    }
  ].filter(Boolean)

  // Every breakpoint is set (an unset one falls back to 3 columns) and labels sit above values, so
  // the summary still reads in the narrower onboarding column.
  return <Descriptions layout="vertical" colon={false} column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} size="small" items={items} />
}

StatusSummary.propTypes = {
  account: PropTypes.shape({
    isConnected: PropTypes.bool,
    connectionStatus: PropTypes.string,
    billingStanding: PropTypes.string,
    isTokenizationEnabled: PropTypes.bool,
    pageSet: PropTypes.string,
    pageName: PropTypes.string,
    verifiedAt: PropTypes.string,
    idLast4: PropTypes.string,
    checkoutPaymentProvider: PropTypes.string
  }).isRequired
}

export default StatusSummary
